import { useEffect, useRef, useState } from 'react';
import { Grid } from '@/components/Grid';
import Script from 'next/script';
import type { GenericQualifio as TGenericQualifio } from 'server/repository/hygraph/generated/graphqlTypes';
import type { ISharedTemplateProps } from 'templates/TemplateMapper/types';
import { QualifioService } from '@/service/qualifio/qualifioService';

interface IGenericQualifio extends ISharedTemplateProps {
  templateProps: TGenericQualifio;
}

export const GenericQualifio = ({ templateProps }: IGenericQualifio) => {
  const [targetId, setTargetId] = useState<string | null>(null);
  const [params, setParams] = useState<string[][]>([]);
  const [iframeId, setIframeId] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  // Initialize the MutationObserver to watch for changes in the wrapper element
  // and set the iframe ID when the Qualifio iframe is added to the DOM.
  const initObserver = () => {
    if (wrapperRef.current) {
      const config = { attributes: false, childList: true, subtree: false };

      const callback = (mutationList: MutationRecord[]) => {
        for (const mutation of mutationList) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              // Check if the added node is an iframe and has the expected ID
              if (
                node instanceof HTMLElement &&
                node.tagName.toLowerCase() === 'iframe' &&
                node.id.includes('qualifio')
              ) {
                setIframeId(node.id);
              }
            });
          }
        }
      };

      observerRef.current = new MutationObserver(callback);
      observerRef.current.observe(wrapperRef.current, config);
    }
  };

  useEffect(() => {
    if (templateProps.widgetCode) {
      const params = QualifioService.getPushParamsFromString(templateProps.widgetCode);
      if (params) {
        const targetId = QualifioService.getTargetId(params);
        setTargetId(targetId);
        setParams(params);
      }
    }
  }, [templateProps.widgetCode]);

  // Set iframe title from Qualifio
  useEffect(() => {
    if (!iframeId) return;

    const handleMessage = (event: MessageEvent) => {
      if (typeof event.origin !== 'string' || !event.origin.includes('qualifio')) return;

      const title = event.data?.title || event.data?.campaign?.title || event.data?.data?.title;

      if (!title) return;

      const iframe = document.getElementById(iframeId);
      if (iframe instanceof HTMLIFrameElement) {
        iframe.setAttribute('title', title);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeId]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const onScriptReady = () => {
    if (window._qual_async && params.length > 0) {
      initObserver();
      params.forEach((arr) => {
        window._qual_async.push(arr);
      });
    }
  };

  const shouldRenderWidget = targetId && params.length > 0;

  if (!shouldRenderWidget) return null;

  return (
    <>
      <Script
        strategy="afterInteractive"
        id="qualifio-script"
        onReady={onScriptReady}
        src="https://files.qualifio.com/kit/qualp.2.min.js"
      />
      <Grid>
        <div id={targetId} className="qualifio_iframe_wrapper" ref={wrapperRef} />
      </Grid>
    </>
  );
};
